import { Link, Outlet, Route, Routes } from "react-router-dom";
import Cockpit from "./pages/Cockpit";
import Governance from "./pages/Governance";
import RuleDetail from "./pages/RuleDetail";
import AgentDetail from "./pages/AgentDetail";
import ClientDashboard from "./pages/ClientDashboard";
import Console from "./pages/Console";
import CustomerMode from "./pages/CustomerMode";

/**
 * Phase E1 subdomain routing — detect tenant slug from hostname.
 *
 * When deployed at {slug}.growtify.app, the React app renders CustomerMode
 * directly at the root path "/" — keeping the URL clean (no /customer/{slug}
 * suffix in the address bar). Same React bundle serves all surfaces.
 *
 * Reserved subdomains that DO NOT trigger customer mode:
 *   ops, www, growtify, app, preview
 */
const RESERVED_SUBDOMAINS = new Set(["ops", "www", "app", "growtify", "preview", ""]);

export function detectTenantSlugFromHostname(): string | null {
  if (typeof window === "undefined") return null;
  const hostname = window.location.hostname.toLowerCase();
  const match = hostname.match(/^([a-z0-9-]+)\.growtify\.app$/);
  if (!match) return null;
  const slug = match[1];
  if (RESERVED_SUBDOMAINS.has(slug)) return null;
  return slug;
}

/** Operator-mode layout (ops.growtify.app surface). */
function OperatorLayout() {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="text-lg font-semibold text-growtify-900">
            ops.growtify.app <span className="text-gray-400 text-sm font-normal">v2 — Phase F MVP</span>
          </Link>
          <div className="flex gap-6 text-sm">
            <Link to="/" className="text-gray-700 hover:text-growtify-500">Cockpit</Link>
            <Link to="/governance" className="text-gray-700 hover:text-growtify-500">Governance</Link>
            <Link to="/console" className="text-gray-700 hover:text-growtify-500">Console</Link>
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        <Outlet />
      </main>

      <footer className="border-t border-gray-200 bg-white py-4 text-center text-xs text-gray-500">
        Growtify Env Backend v0.6.0 · D1 growtify-prod · 18 tables · Phase F MVP · {new Date().toISOString().split("T")[0]}
      </footer>
    </div>
  );
}

export default function App() {
  const subdomainSlug = detectTenantSlugFromHostname();

  // Tenant subdomain mode — clean URL, all paths serve CustomerMode
  // (browser address bar stays at fllat.growtify.app/ — no /customer/fllat suffix)
  if (subdomainSlug) {
    return (
      <Routes>
        <Route path="*" element={<CustomerMode />} />
      </Routes>
    );
  }

  // Operator mode (ops.growtify.app surface, also growtify-env-web.pages.dev)
  return (
    <Routes>
      {/* Operator-accessible per-tenant view */}
      <Route path="/customer/:slug" element={<CustomerMode />} />

      {/* Operator-mode shell */}
      <Route element={<OperatorLayout />}>
        <Route path="/" element={<Cockpit />} />
        <Route path="/governance" element={<Governance />} />
        <Route path="/governance/rules/:name" element={<RuleDetail />} />
        <Route path="/governance/agents/:name" element={<AgentDetail />} />
        <Route path="/clients/:slug" element={<ClientDashboard />} />
        <Route path="/console" element={<Console />} />
      </Route>
    </Routes>
  );
}
